import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import SchoolIcon from '@mui/icons-material/School';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axios from 'axios';
import { NotificationData, NOTIFICATION_TEMPLATES, getNotificationDisplayName } from '../../types/notification';
import { getPrefectureName } from '../../constants/prefectures';
import { useEventDetail } from '../../hooks/useEventDetail';
import { EventData, LOCATION_TYPE_LABELS, INDUSTRY_LABELS, COMPANY_SCALE_LABELS } from '../../types/event';
import { CategoryBadge } from '../events/CategoryBadge';
import { DeadlineLabel } from '../events/DeadlineLabel';

const DAY_OF_WEEK = ['日', '月', '火', '水', '木', '金', '土'];

const formatEventDate = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}月${d.getDate()}日（${DAY_OF_WEEK[d.getDay()]}）`;
};

const formatTime = (t: string): string => t.slice(0, 5);

const formatDatetime = (iso: string): string => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}/${mo}/${day} ${h}:${mi}`;
};

interface InfoRowProps {
  icon?: React.ReactNode;
  label: string;
  value?: string | React.ReactNode;
  isEmpty?: boolean;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value, isEmpty }) => (
  <Box display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
    {icon
      ? <Box sx={{ color: 'primary.main', mt: '2px', flexShrink: 0, fontSize: 18 }}>{icon}</Box>
      : <Box sx={{ width: 18, flexShrink: 0 }} />
    }
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.25} fontWeight={500}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        color={isEmpty ? 'text.secondary' : 'text.primary'}
        fontStyle={isEmpty ? 'italic' : 'normal'}
      >
        {value ?? '未設定'}
      </Typography>
    </Box>
  </Box>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <Box mb={2.5}>
    <Typography variant="subtitle1" fontWeight={700} mb={1.5}>{title}</Typography>
    {children}
  </Box>
);

interface StudentDetailProfile {
  id: number;
  fullName: string;
  email: string;
  university?: string;
  faculty?: string;
  phone?: string;
  grade?: number;
  graduationYear?: number;
  currentPrefecture?: string;
  bio?: string;
}

interface StudentTabProps {
  userId: number;
}

const StudentTab: React.FC<StudentTabProps> = ({ userId }) => {
  const [profile, setProfile] = useState<StudentDetailProfile | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    axios
      .get(`/api/company/notifications/applicant/${userId}`, { withCredentials: true })
      .then((res) => {
        if (!cancelled) setProfile(res.data.data?.student ?? res.data.data ?? res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        let msg = 'プロフィールの取得に失敗しました';
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 404) msg = 'ユーザーが見つかりません';
          else msg = err.response?.data?.error?.message || msg;
        }
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>;
  if (!profile) return null;

  return (
    <Box>
      <Section title="申込者情報">
        <InfoRow icon={<PersonIcon sx={{ fontSize: 16 }} />} label="名前"      value={profile.fullName} />
        <InfoRow icon={<PhoneIcon  sx={{ fontSize: 16 }} />} label="電話番号"  value={profile.phone}    isEmpty={!profile.phone} />
        <InfoRow icon={<EmailIcon  sx={{ fontSize: 16 }} />} label="メール"    value={profile.email} />
        <InfoRow icon={<SchoolIcon sx={{ fontSize: 16 }} />} label="大学"      value={profile.university} isEmpty={!profile.university} />
        <InfoRow label="学部/学科"   value={profile.faculty}    isEmpty={!profile.faculty} />
        {profile.grade          && <InfoRow label="学年"    value={`${profile.grade}年生`} />}
        {profile.graduationYear && <InfoRow label="卒業年度" value={`${profile.graduationYear}年卒`} />}
        {profile.currentPrefecture && <InfoRow icon={<LocationOnIcon sx={{ fontSize: 16 }} />} label="居住地" value={getPrefectureName(profile.currentPrefecture) || profile.currentPrefecture} />}
      </Section>
    </Box>
  );
};

interface EventTabProps {
  eventId: number;
}

const EventTab: React.FC<EventTabProps> = ({ eventId }) => {
  const { event, isLoading, error, fetch, clear } = useEventDetail();

  useEffect(() => {
    fetch(eventId);
    return () => clear();
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>;
  if (!event) return null;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <CategoryBadge category={event.category} />
      </Stack>

      <Typography variant="h6" fontWeight={700}>{event.title}</Typography>

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <BusinessIcon sx={{ color: 'text.secondary', fontSize: 18, mt: '2px', flexShrink: 0 }} />
        <Box>
          <Typography variant="body2">{event.company_name}</Typography>
          {(event.industry || event.company_scale) && (
            <Typography variant="caption" color="text.secondary">
              {[
                event.industry      && INDUSTRY_LABELS[event.industry],
                event.company_scale && COMPANY_SCALE_LABELS[event.company_scale],
              ].filter(Boolean).join('・')}
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Typography variant="body2">
          {formatEventDate(event.event_date)}　{formatTime(event.start_time)}〜{formatTime(event.end_time)}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Typography variant="body2">
          {LOCATION_TYPE_LABELS[event.location_type]}　{event.location}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <PeopleIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Typography variant="body2">定員 {event.capacity}名</Typography>
      </Stack>

      <Box>
        <DeadlineLabel event={event} />
      </Box>

      {event.description && (
        <>
          <Divider />
          <Typography variant="body2" whiteSpace="pre-wrap">{event.description}</Typography>
        </>
      )}
    </Stack>
  );
};

interface CompanyNotificationDetailDialogProps {
  open: boolean;
  onClose: () => void;
  notification: NotificationData | null;
  onToggleHandled?: (ntf: NotificationData) => void;
}

export const CompanyNotificationDetailDialog: React.FC<CompanyNotificationDetailDialogProps> = ({
  open,
  onClose,
  notification,
  onToggleHandled,
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (open) setTab(0);
  }, [open, notification?.id]);

  if (!notification) return null;

  const isApplication = notification.type === 'application';
  const summaryBgColor     = isApplication ? 'primary.50'  : 'error.50';
  const summaryBorderColor = isApplication ? 'primary.100' : 'error.100';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '5%',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="h6" fontWeight={700}>申込詳細</Typography>
          <IconButton size="small" onClick={onClose} sx={{ ml: 1, flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* 通知サマリー */}
      <Box
        sx={{
          mx: 2.5, mb: 1.5, px: 2, py: 1.25,
          bgcolor: summaryBgColor,
          border: '1px solid', borderColor: summaryBorderColor,
          borderRadius: 1.5,
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
          {formatDatetime(notification.created_at)}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {NOTIFICATION_TEMPLATES[notification.type](getNotificationDisplayName(notification), notification.event.title)}
        </Typography>
      </Box>

      {/* タブ */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2.5 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab
            icon={<PersonIcon sx={{ fontSize: 18 }} />}
            iconPosition="top"
            label="学生情報"
            sx={{ fontWeight: 600, minHeight: 64, fontSize: '0.8rem', py: 1 }}
          />
          <Tab
            icon={<EventIcon sx={{ fontSize: 18 }} />}
            iconPosition="top"
            label="イベント詳細"
            sx={{ fontWeight: 600, minHeight: 64, fontSize: '0.8rem', py: 1 }}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2.5, px: 2.5, minHeight: 320 }}>
        {tab === 0 && (
          notification.student
            ? <StudentTab userId={notification.student.id} />
            : <Typography color="text.secondary">学生情報がありません</Typography>
        )}
        {tab === 1 && <EventTab eventId={notification.event.id} />}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        {onToggleHandled && (
          <Button
            variant="outlined"
            color={notification.is_handled ? 'inherit' : 'success'}
            onClick={() => onToggleHandled(notification)}
            sx={{ mr: 'auto' }}
          >
            {notification.is_handled ? '対応済みを取消' : '✓ 対応済みにする'}
          </Button>
        )}
        <Button onClick={onClose} variant="outlined">閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};
